import { Trans } from '@lingui/macro';
import { createContext, useContext, useState } from "react";
import { Message } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { AddFromMyRoomFilesModal } from "../AddFromMyRoomFilesModal";
import { ScreenRecordModalEmitter } from "../ScreenRecordModal";
import { VoiceRecordModalEmitter } from "./MsgRecordModal";
import { RoomItem } from "../../../models/RoomItem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { Plus, Video, Mic, Paperclip, FileText, X } from "lucide-react";

const MsgAddMorePopover: React.FC<{ room: Room, parentMessage: Message | undefined, children?: React.ReactNode }> = ({ room, parentMessage, children }) => {
  const { files, setFiles, setItems } = useContext(UploadFilesContext);
  const [showMyRoomModal, setShowMyRoomModal] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {children ? children : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-[#f2f2f2] hover:bg-[#e5e5e5] text-[#939393] hover:text-[#555] hover:bg-gray-100 transition-colors"
              data-cy="plus-btn"
            >
              <Plus className="h-5 w-5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={5} className="w-56 z-[2000]">
          <DropdownMenuItem onClick={recordVideo} className="cursor-pointer py-3">
            <Video className="mr-3 h-5 w-5 text-gray-700" />
            <Trans>Screen Recording</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={recordAudio} className="cursor-pointer py-3">
            <Mic className="mr-3 h-5 w-5 text-gray-700" />
            <Trans>Voice Recording</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={selectFilesToUpload} className="cursor-pointer py-3">
            <Paperclip className="mr-3 h-5 w-5 text-gray-700" />
            <Trans>Upload Files</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={addFromMyRoom} className="cursor-pointer py-3">
            <FileText className="mr-3 h-5 w-5 text-gray-700" />
            <Trans>Add from My Room</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpen(false)} className="cursor-pointer py-3" data-cy="cancel">
            <X className="mr-3 h-5 w-5 text-gray-700" />
            <Trans>Cancel</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input id="file-input" type='file' onChange={filesSelected} hidden multiple />
      {showMyRoomModal && <AddFromMyRoomFilesModal onCancel={() => setShowMyRoomModal(false)} onDone={doneAddFromMyRoomFiles} />}
    </>
  )

  function recordVideo() {
    ScreenRecordModalEmitter.startRecording(room, parentMessage);
    setOpen(false);
  }

  function recordAudio() {
    VoiceRecordModalEmitter.startRecording();
    setOpen(false);
  }

  function selectFilesToUpload() {
    setOpen(false);
    document.getElementById('file-input')?.click();
  }

  async function filesSelected(e: any) {
    const cnt = e.target.files.length;
    let newFiles: File[] = [];
    for (let i = 0; i < cnt; i++) {
      const file = e.target.files[i];
      if (files.find(f => f.name === file.name)) {
        continue;
      }
      newFiles.push(file);
    }
    if (e.target.files) {
      setFiles([...newFiles, ...files]);
      e.target.value = '';
    }
  }

  function addFromMyRoom() {
    setOpen(false);
    setShowMyRoomModal(true);
  }

  function doneAddFromMyRoomFiles(selectedItems: RoomItem[]) {
    setShowMyRoomModal(false);
    setItems(selectedItems);
  }
}

type IUploadFilesContext = {
  files: File[], setFiles: (files: File[]) => void,
  items: RoomItem[], setItems: (items: RoomItem[]) => void
}
export const UploadFilesContext = createContext<IUploadFilesContext>({
  files: [],
  setFiles: (p: File[]) => { },
  items: [],
  setItems: (p: RoomItem[]) => { }
});

export default MsgAddMorePopover;
